import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Box, CircularProgress } from '@mui/material';
import { FormattedMessage, unsafeTranslationKey } from 'intl';
import { FileTranslationKeys } from 'intl/keys';

import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { useImageNaturalDimensions } from 'hooks/useImageNaturalDimensions';
import { noop } from 'shared/utils/function';
import { CounterScaleProvider } from 'components/location/pin/counterScaleContext';
import { ResponsiveImage } from './components/ResponsiveImage';
import {
  resolveImageBounds,
  resolveImageScale,
  resolveMinimumImageScale,
  resolvePanPinchWrapperStyle,
  resolveTransformKey,
} from './model';
import { ERROR_CONTAINER_STYLE, PREPARING_IMAGE_SX } from './styles';
import { ImageBounds, ResponsivePanPinchZoomProps } from './types';

const ImageBoundsContext = createContext<
  undefined | ImageBounds
>(undefined);

export function ResponsivePanPinchZoom({
  scaleUp,
  zoomFactor = 20,
  onTransformed,
  transformOptions,
  imageRef,
  imageSrc,
  imageLoading,
  imageError,
  onImageLoad,
  container,
  children,
  minimumScaleFactor = 0,
  imageRotation,
}: ResponsivePanPinchZoomProps): JSX.Element | null {
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);

  const {
    width: imageNaturalWidth,
    height: imageNaturalHeight,
    failed: imageLoadFailed,
  } = useImageNaturalDimensions(imageSrc);

  const imageScale = useMemo(
    () =>
      resolveImageScale({
        scaleUp,
        containerWidth,
        containerHeight,
        imageNaturalWidth,
        imageNaturalHeight,
        imageRotation,
      }),
    [
      scaleUp,
      containerWidth,
      containerHeight,
      imageNaturalWidth,
      imageNaturalHeight,
      imageRotation,
    ]
  );

  const minimumImageScale = useMemo(() => {
    return resolveMinimumImageScale(imageScale, minimumScaleFactor);
  }, [imageScale, minimumScaleFactor]);

  const handleResize = useCallback(() => {
    if (container !== null) {
      const rect = container.getBoundingClientRect();
      setContainerWidth(rect.width);
      setContainerHeight(rect.height);
    } else {
      setContainerWidth(0);
      setContainerHeight(0);
    }
  }, [container]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);

    if (!container) {
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });
    resizeObserver.observe(container);

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [container, handleResize]);

  const maxScale = imageScale * zoomFactor;
  const transformKey = resolveTransformKey(
    containerWidth,
    containerHeight,
    imageRotation
  );

  const wrapperStyle = useMemo(
    () =>
      resolvePanPinchWrapperStyle(
        containerWidth,
        containerHeight
      ),
    [containerWidth, containerHeight]
  );

  const imageContext = useMemo(
    () =>
      resolveImageBounds(
        imageNaturalWidth,
        imageNaturalHeight,
        imageRotation
      ),
    [imageNaturalWidth, imageNaturalHeight, imageRotation]
  );

  if (!imageLoading && imageError) {
    return (
      <div style={ERROR_CONTAINER_STYLE}>
        <FormattedMessage id={unsafeTranslationKey(imageError)} />
      </div>
    );
  }

  if (imageLoadFailed && imageSrc) {
    return (
      <div style={ERROR_CONTAINER_STYLE}>
        <FormattedMessage id={FileTranslationKeys.fileUploadErrorGeneral} />
      </div>
    );
  }

  const isPreparingImage = Boolean(imageSrc) && imageScale === 0;

  if (isPreparingImage) {
    return (
      <Box sx={PREPARING_IMAGE_SX}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (imageScale <= 0) {
    return null;
  }

  return (
    <TransformWrapper
      onTransformed={
        typeof onTransformed === 'function' ? onTransformed : noop
      }
      key={transformKey}
      initialScale={minimumImageScale}
      minScale={minimumImageScale}
      maxScale={maxScale}
      doubleClick={transformOptions?.doubleClick}
      wheel={transformOptions?.wheel}
      centerOnInit
      limitToBounds
      centerZoomedOut
      disablePadding
      alignmentAnimation={{ sizeX: 0, sizeY: 0 }}
      velocityAnimation={{ disabled: true }}
    >
      <TransformComponent
        key={transformKey}
        wrapperStyle={wrapperStyle}
      >
        {!imageLoading && imageSrc && (
          <ResponsiveImage
            imageRef={imageRef}
            imageSrc={imageSrc}
            handleImageOnLoad={noop}
            onImageLoad={onImageLoad}
            imageRotation={imageRotation}
            imageNaturalWidth={imageNaturalWidth}
            imageNaturalHeight={imageNaturalHeight}
          />
        )}
        <CounterScaleProvider>
          <ImageBoundsContext.Provider value={imageContext}>
            {children}
          </ImageBoundsContext.Provider>
        </CounterScaleProvider>
      </TransformComponent>
    </TransformWrapper>
  );
}

export function useImageBoundsContext(): {
  width: number;
  height: number;
} {
  const context = useContext(ImageBoundsContext);
  if (context === undefined) {
    throw new Error(
      'useImageBoundsContext must be used within an ImageBoundsContextProvider'
    );
  }
  return context;
}
